import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, FileText, Loader2, RefreshCw, Sparkles, Trash2, Upload, X } from 'lucide-react';
import { describeError } from '../../services/errorMessage';
import { holdIdleExit } from '../../services/idleHold';
import { liesMerkmale } from '../../services/planungKi/planungKiService';
import { bereiteQuelleVor } from '../../services/planungKi/quelle';
import type { KiQuelle } from '../../services/planungKi/typen';
import { ladeDokumentHerunter } from '../../services/projektDokumenteService';
import { formatBytes, kategorieLabel } from '../dokumente/dokumenteLogic';
import { useDokumente } from '../dokumente/useDokumente';
import {
    KI_MAX_DOKUMENTE,
    KI_MAX_LABEL,
    KI_MAX_SCAN_LABEL,
    KI_MAX_SEITEN,
    kiAblageAuswahl,
    kiDateiSperrgrund,
    kiDokumentStandAus,
    kiGesendeteSeiten,
    kiMerkmalAnfragen,
    type KiDokumentStand,
    type KiLeseErgebnis,
} from './planungKiAnzeige';
import type { ProjektMerkmale } from './types';

interface Props {
    projectId: string;
    /** Merkmale des ENTWURFS — gefragt wird nur, was dort noch nicht gesetzt ist. */
    merkmale: ProjektMerkmale;
    isReadOnly: boolean;
    /** Ergebnis eines Laufs (auch eines gescheiterten) — der Dialog schliesst danach. */
    onFertig: (ergebnis: KiLeseErgebnis) => void;
    onSchliessen: () => void;
}

/** Eine lokal gewaehlte Datei. Sie wird nur gelesen, nie abgelegt. */
interface LokaleDatei {
    key: string;
    datei: File;
    sperrgrund: string | null;
}

/** Was in einem Lauf an die KI soll — Ablage und lokale Dateien in einer Liste. */
interface Ziel {
    name: string;
    herkunft: KiDokumentStand['herkunft'];
    lade: () => Promise<Uint8Array>;
}

const KNOPF = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed';

/**
 * „Aus Unterlagen lesen (KI)" (Plan Stufe 7.1) — Auswahl und Lauf.
 *
 * Auswahl: PDFs der Projektablage (`kiAblageAuswahl`: nie Betriebsanweisungen
 * und Gefaehrdungen, nie admin-gesperrte, keine eigenen Unterlagen der App)
 * und/oder lokale PDFs, die nur gelesen und NICHT abgelegt werden. Menge und
 * Groesse sind begrenzt und stehen im Dialog.
 *
 * Lauf: je Dokument laden → `bereiteQuelleVor` (Textebene, sonst Scan) →
 * `liesMerkmale` mit den Anfragen fuer die noch ungesetzten Merkmale.
 * Abbrechen beendet den Lauf (AbortSignal); eine spaete Antwort wird ueber die
 * Laufnummer verworfen. Ein Dokument, das nicht gelesen werden kann, fehlt im
 * Lauf und steht mit Grund im Ergebnis — nie still.
 *
 * Der Dialog schreibt nichts: das Ergebnis geht an die Leiste, uebernommen
 * wird dort je Zeile in den Entwurf.
 */
export const KiUnterlagenDialog: React.FC<Props> = ({ projectId, merkmale, isReadOnly, onFertig, onSchliessen }) => {
    const ablage = useDokumente(projectId);
    const auswahl = useMemo(() => kiAblageAuswahl(ablage.dokumente), [ablage.dokumente]);
    const anfragen = useMemo(() => kiMerkmalAnfragen(merkmale), [merkmale]);

    const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
    const [dateien, setDateien] = useState<LokaleDatei[]>([]);
    const [schritt, setSchritt] = useState<string | null>(null);
    const [hinweis, setHinweis] = useState<string | null>(null);
    const dateiFeld = useRef<HTMLInputElement | null>(null);
    const laufNr = useRef(0);
    const steuerung = useRef<AbortController | null>(null);

    // Solange der Dialog offen ist, darf der Leerlauf-Rueckzug das Projekt
    // nicht schliessen — und ein Abbau beendet einen laufenden Lauf.
    useEffect(() => {
        const freigeben = holdIdleExit('planungsphase-ki-unterlagen');
        return () => {
            freigeben();
            laufNr.current += 1;
            steuerung.current?.abort();
        };
    }, []);

    const laeuft = schritt !== null;
    const gueltigeDateien = dateien.filter(d => !d.sperrgrund);
    const anzahl = gewaehlt.size + gueltigeDateien.length;
    const voll = anzahl >= KI_MAX_DOKUMENTE;

    const umschalten = (id: string) => {
        setHinweis(null);
        setGewaehlt(alt => {
            const neu = new Set(alt);
            if (neu.has(id)) neu.delete(id);
            else if (neu.size + gueltigeDateien.length < KI_MAX_DOKUMENTE) neu.add(id);
            return neu;
        });
    };

    const dateienGewaehlt = (liste: FileList | null) => {
        if (!liste || !liste.length) return;
        setHinweis(null);
        let frei = KI_MAX_DOKUMENTE - anzahl;
        const neu: LokaleDatei[] = [];
        let uebrig = 0;
        Array.from(liste).forEach((datei, i) => {
            const sperrgrund = kiDateiSperrgrund(datei.name, datei.type, datei.size);
            if (!sperrgrund && frei <= 0) { uebrig++; return; }
            if (!sperrgrund) frei--;
            neu.push({ key: `${Date.now()}-${i}-${datei.name}`, datei, sperrgrund });
        });
        setDateien(alt => [...alt, ...neu]);
        if (uebrig) setHinweis(`${uebrig} Datei(en) nicht aufgenommen: höchstens ${KI_MAX_DOKUMENTE} Dokumente je Lauf.`);
    };

    const abbrechen = () => {
        laufNr.current += 1;
        steuerung.current?.abort();
        steuerung.current = null;
        setSchritt(null);
        setHinweis('Abgebrochen. Die KI-Auswertung wurde beendet, nichts wurde übernommen.');
    };

    const starte = async () => {
        if (isReadOnly || laeuft || !anfragen.length) return;
        const ziele: Ziel[] = [
            ...auswahl.angeboten
                .filter(a => gewaehlt.has(a.dokument.id) && !a.sperrgrund)
                .map((a): Ziel => ({
                    name: a.dokument.titel || a.dokument.dateiname,
                    herkunft: 'ablage',
                    lade: async () => new Uint8Array(await (await ladeDokumentHerunter(a.dokument.storage_pfad)).arrayBuffer()),
                })),
            ...gueltigeDateien.map((d): Ziel => ({
                name: d.datei.name,
                herkunft: 'datei',
                lade: async () => new Uint8Array(await d.datei.arrayBuffer()),
            })),
        ];
        if (!ziele.length || ziele.length > KI_MAX_DOKUMENTE) return;

        const nr = ++laufNr.current;
        const steuer = new AbortController();
        steuerung.current = steuer;
        const gilt = () => laufNr.current === nr && !steuer.signal.aborted;
        setHinweis(null);

        const quellen: KiQuelle[] = [];
        const dokumente: KiDokumentStand[] = [];
        try {
            for (let i = 0; i < ziele.length; i++) {
                const z = ziele[i];
                setSchritt(`Dokument ${i + 1} von ${ziele.length} wird vorbereitet: ${z.name}`);
                try {
                    const bytes = await z.lade();
                    if (!gilt()) return;
                    const quelle = await bereiteQuelleVor(bytes, z.name);
                    if (!gilt()) return;
                    quellen.push(quelle);
                    dokumente.push(kiDokumentStandAus(quelle, z.name, z.herkunft, kiGesendeteSeiten(quelle, anfragen)));
                } catch (e) {
                    if (!gilt()) return;
                    dokumente.push({
                        name: z.name, herkunft: z.herkunft, art: null, seitenMitText: 0, gelesenSeiten: null, seitenGesamt: null, ohneTextSeiten: [], fehler: describeError(e),
                    });
                }
            }

            const basis = { projectId, gelesenAm: new Date().toISOString(), anfragen, dokumente };
            if (!quellen.length) {
                onFertig({ ...basis, lauf: null, fehler: 'Keines der Dokumente konnte gelesen werden, die KI wurde nicht befragt.' });
                return;
            }

            setSchritt(`Die KI liest ${quellen.length} Dokument${quellen.length === 1 ? '' : 'e'} und sucht Belege zu ${anfragen.length} Merkmalen …`);
            try {
                const lauf = await liesMerkmale(quellen, anfragen, { signal: steuer.signal });
                if (!gilt()) return;
                onFertig({ ...basis, lauf, fehler: null });
            } catch (e) {
                if (!gilt()) return;
                onFertig({ ...basis, lauf: null, fehler: `Die KI-Auswertung ist fehlgeschlagen: ${describeError(e)}` });
            }
        } finally {
            if (laufNr.current === nr) {
                steuerung.current = null;
                setSchritt(null);
            }
        }
    };

    const sendbar = !isReadOnly && !laeuft && anzahl > 0 && anzahl <= KI_MAX_DOKUMENTE && anfragen.length > 0;

    return createPortal(
        <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200">
                    <Sparkles className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-slate-800">Aus Unterlagen lesen (KI)</h3>
                        <p className="text-xs text-slate-500 leading-snug">
                            Baubeschreibung, Baugenehmigung, Gutachten oder LV: Die KI sucht Belege für die noch offenen
                            Merkmale und schlägt Werte mit wörtlichem Zitat vor.
                        </p>
                        <p className="text-[11px] text-slate-400 leading-snug mt-0.5">
                            Grenzen: höchstens {KI_MAX_DOKUMENTE} Dokumente je Lauf, je PDF höchstens {KI_MAX_LABEL} und
                            {' '}{KI_MAX_SEITEN} Seiten; ein Scan ohne Textebene geht als ganze Datei an die KI und darf
                            höchstens {KI_MAX_SCAN_LABEL} groß sein.
                        </p>
                    </div>
                    <button type="button" onClick={onSchliessen} disabled={laeuft} className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-40" aria-label="Schließen">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-4 overflow-y-auto">
                    {anfragen.length === 0 && (
                        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                            Alle Merkmale sind gesetzt, es gibt nichts zu fragen. Ein ausdrückliches Ja oder Nein wird nicht
                            von der KI überprüft.
                        </div>
                    )}

                    {/* ─── Projektablage ─────────────────────────────── */}
                    <div>
                        <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex-1">Aus der Projektablage</span>
                            <span className={`text-[11px] font-bold ${voll ? 'text-amber-700' : 'text-slate-400'}`}>
                                {anzahl} von {KI_MAX_DOKUMENTE} gewählt
                            </span>
                        </div>
                        {ablage.laden && <p className="text-xs text-slate-400 py-2">Ablage wird geladen …</p>}
                        {ablage.fehler && (
                            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                <span className="flex-1">Die Projektablage konnte nicht gelesen werden: {ablage.fehler}</span>
                                <button type="button" onClick={() => { void ablage.neuLaden(); }} className="inline-flex items-center gap-1 font-bold underline shrink-0">
                                    <RefreshCw className="w-3 h-3" /> Erneut laden
                                </button>
                            </div>
                        )}
                        {!ablage.laden && !ablage.fehler && auswahl.angeboten.length === 0 && (
                            <p className="text-xs text-slate-500 py-1">In der Ablage liegt kein PDF, das an die KI gehen darf.</p>
                        )}
                        {auswahl.angeboten.length > 0 && (
                            <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
                                {auswahl.angeboten.map(({ dokument: d, sperrgrund }) => {
                                    const an = gewaehlt.has(d.id);
                                    const gesperrt = !!sperrgrund || laeuft || isReadOnly || (!an && voll);
                                    return (
                                        <li key={d.id}>
                                            <label className={`flex items-start gap-2.5 px-3 py-2 ${gesperrt && !an ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={an}
                                                    disabled={gesperrt && !an}
                                                    onChange={() => umschalten(d.id)}
                                                    className="mt-0.5 w-4 h-4 rounded border-slate-300 shrink-0"
                                                />
                                                <FileText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-sm text-slate-800 truncate">{d.titel || d.dateiname}</span>
                                                    <span className="block text-[11px] text-slate-400">
                                                        {kategorieLabel(d.kategorie)}{d.ordner ? ` / ${d.ordner}` : ''} · {formatBytes(d.groesse_bytes)}
                                                    </span>
                                                    {sperrgrund && <span className="block text-[11px] font-bold text-amber-700">{sperrgrund}</span>}
                                                </span>
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {auswahl.nichtAngeboten.length > 0 && (
                            <p className="text-[11px] text-slate-400 mt-1.5 leading-snug">
                                Nicht angeboten: {auswahl.nichtAngeboten.map(z => `${z.anzahl} × ${z.grund}`).join(' · ')}.
                            </p>
                        )}
                    </div>

                    {/* ─── Lokale Dateien ────────────────────────────── */}
                    <div>
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5">Datei von diesem Rechner</div>
                        <input
                            ref={dateiFeld}
                            type="file"
                            accept="application/pdf,.pdf"
                            multiple
                            className="hidden"
                            onChange={e => { dateienGewaehlt(e.target.files); e.target.value = ''; }}
                        />
                        <button
                            type="button"
                            onClick={() => dateiFeld.current?.click()}
                            disabled={laeuft || isReadOnly || voll}
                            className={`${KNOPF} text-slate-700 bg-white border-slate-300 hover:bg-slate-50`}
                        >
                            <Upload className="w-3.5 h-3.5" /> PDF wählen …
                        </button>
                        <span className="ml-2 text-[11px] text-slate-400">Wird nur gelesen, nicht in der Ablage gespeichert.</span>
                        {dateien.length > 0 && (
                            <ul className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100">
                                {dateien.map(d => (
                                    <li key={d.key} className="flex items-start gap-2.5 px-3 py-2">
                                        <FileText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm text-slate-800 truncate">{d.datei.name}</span>
                                            <span className="block text-[11px] text-slate-400">{formatBytes(d.datei.size)}</span>
                                            {d.sperrgrund && <span className="block text-[11px] font-bold text-amber-700">{d.sperrgrund} Wird nicht gesendet.</span>}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setDateien(alt => alt.filter(x => x.key !== d.key))}
                                            disabled={laeuft}
                                            className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-40"
                                            aria-label={`${d.datei.name} entfernen`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* ─── Was an die KI geht ────────────────────────── */}
                    <div className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 leading-snug">
                        <strong>Mit „An die KI senden“ gehen die ausgewählten Dokumente zur Auswertung an die KI</strong>, bei
                        PDFs mit Textebene nur der Text, bei Scans die ganze Datei. Gefragt wird nach {anfragen.length} noch
                        offenen Merkmal{anfragen.length === 1 ? '' : 'en'}; gesetzte Felder bleiben unberührt. Die KI liest
                        nur und schlägt vor; übernommen wird nichts von selbst.
                    </div>

                    {hinweis && (
                        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{hinweis}</div>
                    )}
                    {laeuft && (
                        <div className="flex items-center gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                            <span className="flex-1 min-w-0">{schritt}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
                    {laeuft ? (
                        <button type="button" onClick={abbrechen} className={`${KNOPF} text-rose-700 bg-white border-rose-200 hover:bg-rose-50`}>
                            <X className="w-3.5 h-3.5" /> Abbrechen
                        </button>
                    ) : (
                        <button type="button" onClick={onSchliessen} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800">
                            Schließen
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => { void starte(); }}
                        disabled={!sendbar}
                        className={`${KNOPF} text-white bg-blue-600 border-blue-600 hover:bg-blue-700`}
                    >
                        <Sparkles className="w-3.5 h-3.5" /> An die KI senden{anzahl > 0 ? ` (${anzahl})` : ''}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
